"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePlayerSetup = void 0;
const react_1 = require("react");
const api_1 = require("./api");
const postmate_1 = __importDefault(require("postmate"));
const types_1 = require("./types");
const utils_1 = require("./utils");
const LOADING_DELAY = 1000;
const SECOND_VERSION_REGEXP = /V2/gi;
const usePlayerSetup = ({ onFrameDestroy, setSetupOptions, childApiRef, currentTrack, setCurrentTrack, setCurrentPlayerUrl, }) => {
    const [playState, setPlayState] = (0, react_1.useState)(types_1.EPlayState.PAUSE);
    const [volume, setVolume] = (0, react_1.useState)(100);
    const [playlist, setPlaylist] = (0, react_1.useState)([]);
    const [history, setHistory] = (0, react_1.useState)([]);
    const [pointData, setPointData] = (0, react_1.useState)(null);
    const [sleeping, setSleeping] = (0, react_1.useState)(false);
    const [playerIsStalled, setPlayerIsStalled] = (0, react_1.useState)(false);
    const [frameStatus, setFrameStatus] = (0, react_1.useState)(types_1.EFrameStatus.LOADING);
    const [logs, setLogs] = (0, react_1.useState)([]);
    const [safeSwitchZone, setSafeSwitchZone] = (0, react_1.useState)(false);
    const [disliked, setDisliked] = (0, react_1.useState)({});
    const [pointMetadata, setPointMetadata] = (0, react_1.useState)({
        isNoPoint: false,
        pointHasNoStream: false,
        isPointDisabled: false,
    });
    const [playerSettings, setPlayerSettings] = (0, react_1.useState)({});
    const [backgroundCachingEnabled, setBackgroundCachingEnabled] = (0, react_1.useState)(false);
    const [isConnected, setIsConnected] = (0, react_1.useState)(true);
    const [isFileLoading, setIsFileLoading] = (0, react_1.useState)(false);
    const loadingTimerIdRef = (0, react_1.useRef)(null);
    const [generation, setGeneration] = (0, react_1.useState)(null);
    const [applicationLoadingState, setApplicationLoadingState] = (0, react_1.useState)(null);
    const [startPlaybackAttempt, setStartPlaybackAttempt] = (0, react_1.useState)({
        success: false,
        time: null,
        failureReason: null,
    });
    const [sleepInterval, setSleepInterval] = (0, react_1.useState)();
    const setup = (0, react_1.useCallback)((container, bindingCode, apiUrl, styles, frameStyle, settings = {}, rewritePlayerUrl, needRedirect = true, silenceDataUri) => __awaiter(void 0, void 0, void 0, function* () {
        if (!bindingCode)
            return;
        onFrameDestroy();
        setSetupOptions({
            container,
            bindingCode,
            apiUrl,
            styles,
            frameStyle,
            settings,
            rewritePlayerUrl,
            needRedirect,
            silenceDataUri,
        });
        let currentUri = localStorage.getItem((0, utils_1.getUrlKey)(bindingCode));
        try {
            if (!currentUri && apiUrl) {
                currentUri = yield (0, api_1.getWebPlayerUrl)(bindingCode, apiUrl);
            }
        }
        catch (err) {
            console.error(err);
        }
        if (rewritePlayerUrl) {
            currentUri = `${rewritePlayerUrl}${bindingCode}`;
        }
        if (!currentUri) {
            setPointMetadata(Object.assign(Object.assign({}, pointMetadata), { isNoPoint: true }));
            console.error("No player url found");
            setCurrentPlayerUrl("");
            return;
        }
        if (SECOND_VERSION_REGEXP.test(currentUri) && needRedirect) {
            currentUri = currentUri.replace("widget", "point");
            localStorage.removeItem((0, utils_1.getUrlKey)(bindingCode));
            window.location.replace(currentUri);
            return;
        }
        const serializedStyles = JSON.stringify(styles);
        const searchParams = new URLSearchParams();
        searchParams.set("styles", serializedStyles);
        Object.entries(settings).forEach(([label, value]) => {
            searchParams.set(label, value);
        });
        const handshake = new postmate_1.default({
            container,
            url: `${currentUri}?${searchParams.toString()}`,
        });
        try {
            const childFrame = yield handshake;
            childApiRef.current = childFrame;
            Object.entries(frameStyle).forEach(([key, value]) => {
                childFrame.frame.style[key] = value;
            });
            childFrame.on("PLAY_STATE_UPDATE", (state) => setPlayState(state));
            childFrame.on("VOLUME_UPDATE", (nextVolume) => setVolume(nextVolume));
            childFrame.on("PLAYLIST_UPDATE", ({ playlist: nextPlaylist, current, history: newHistory, disliked: newDisliked, generationSettings: newGenerationSettings, }) => {
                setPlaylist(nextPlaylist);
                setCurrentTrack(current);
                setHistory(newHistory);
                setDisliked(newDisliked);
                setGeneration(newGenerationSettings);
            });
            childFrame.on("SLEEP_STATE_CHANGED", (nextSleeping) => {
                setSleeping(nextSleeping);
            });
            childFrame.on("POINT_UPDATED", (point) => {
                setPointData(point);
            });
            childFrame.on("PLAYER_STALLED", (isStalled) => {
                setPlayerIsStalled(isStalled);
            });
            childFrame.on("_LOG_RECORD", (record) => setLogs((prev) => [...prev, record]));
            childFrame.on("SAFE_SWITCH_ZONE_CHANGED", (newSaveSwitchZone) => setSafeSwitchZone(newSaveSwitchZone));
            childFrame.on("SET_POINT_METADATA", (metadata) => setPointMetadata(metadata));
            childFrame.on("START_PLAYBACK_ATTEMPT", (newStartPlaybackAttempt) => {
                setStartPlaybackAttempt(newStartPlaybackAttempt);
            });
            childFrame.on("SLEEP_INTERVAL_CHANGED", (newInterval) => {
                setSleepInterval(newInterval);
            });
            childFrame.on("SETTINGS_UPDATED", (_settings) => {
                setPlayerSettings(_settings);
            });
            childFrame.on("CACHING_STATE_UPDATED", (state) => {
                setBackgroundCachingEnabled(state);
            });
            childFrame.on("CONNECTION_STATUS_CHANGED", (isConencted) => {
                setIsConnected(isConencted);
            });
            childFrame.on("SET_GENERATION_SETTINGS", (newGenerationSettings) => {
                setGeneration(newGenerationSettings);
            });
            childFrame.on("IS_FILE_LOADING", (isLoading) => {
                if (loadingTimerIdRef.current) {
                    clearTimeout(loadingTimerIdRef.current);
                }
                if (isLoading) {
                    loadingTimerIdRef.current = setTimeout(() => {
                        setIsFileLoading(isLoading);
                        loadingTimerIdRef.current = null;
                    }, LOADING_DELAY);
                }
                else {
                    loadingTimerIdRef.current = null;
                    setIsFileLoading(isLoading);
                }
            });
            childFrame.on("APP_LOADING_STATE", (newAppLoadingState) => {
                if (newAppLoadingState) {
                    setApplicationLoadingState(newAppLoadingState);
                }
                childFrame.call("STATE_INIT");
            });
            setFrameStatus(types_1.EFrameStatus.ENABLED);
            setCurrentPlayerUrl(currentUri);
            setTimeout(() => {
                if (childApiRef.current) {
                    childApiRef.current.call("STATE_INIT");
                }
            }, 400);
            return childFrame.frame;
        }
        catch (error) {
            console.error(error);
            setCurrentPlayerUrl("");
            setPointMetadata(Object.assign(Object.assign({}, pointMetadata), { isNoPoint: true }));
            return null;
        }
    }), [onFrameDestroy, pointMetadata]);
    const state = {
        playState,
        volume,
        playlist,
        currentTrack,
        sleeping,
        pointData,
        playerIsStalled,
        history,
        safeSwitchZone,
        disliked,
        pointMetadata,
        startPlaybackAttempt,
        sleepInterval,
        playerSettings,
        backgroundCachingEnabled,
        isConnected,
        isFileLoading,
        generation,
        applicationLoadingState,
    };
    return {
        state,
        setup,
        frameStatus,
    };
};
exports.usePlayerSetup = usePlayerSetup;
//# sourceMappingURL=usePlayerSetup.js.map